import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Collapse,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  CardHeader,
  Divider,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import CreateSignatureDialog from "./CreateSignatureDialog";
import { SAVE_SIGNATURE, UPDATE_AUTO_APPROVE } from "../gql/mutation";
import { useMutation } from "@apollo/client";
import clientGraphql from "../../../common/apollo-graphql";
import { getUser, setUser } from "../../auth/hooks/useAuth";

export default function SignatureManagement({ 
  signature: initialSignature, 
  autoApproved: initialAutoApproved, 
  loading,
  onSignatureChange,
}) {
  const [expanded, setExpanded] = useState(true);
  const [signature, setSignature] = useState(initialSignature);
  const [openDialog, setOpenDialog] = useState(false);
  const [autoApproved, setAutoApproved] = useState(initialAutoApproved);
  const fileInputRef = useRef(null);

  const [saveSignature] = useMutation(SAVE_SIGNATURE, {
    client: clientGraphql,
  });

  const [updateAutoApprove] = useMutation(UPDATE_AUTO_APPROVE, {
    client: clientGraphql,
  });

  const handleSaveSignature = async (dataUrl, name = "Signature") => {
    try {
      await saveSignature({
        variables: {
          signature: dataUrl,
          name,
        },
      });
      setSignature(dataUrl);
      if (onSignatureChange) onSignatureChange(dataUrl);

      if (!initialSignature) {
        const currentUser = getUser();

        if (currentUser) {
          setUser({
            ...currentUser,
            hasSignature: !!dataUrl,
          });
        }
      }
    } catch (err) {
      console.error("Error saving signature:", err);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        handleSaveSignature(e.target.result, file.name);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    setSignature(initialSignature);
    setAutoApproved(initialAutoApproved);
  }, [initialSignature, initialAutoApproved]);

  return (
    <Card variant="outlined" sx={{ padding: 0, borderRadius: 4 }}>
      <CardHeader
        title="Signature Management"
        action={
          <ExpandMoreIcon
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "0.3s",
            }}
          />
        }
        onClick={() => setExpanded(!expanded)}
        sx={{
          cursor: "pointer",
          background: "#fff",
          color: "#000",
          borderBottom: "1px solid",
          borderColor: "divider",
          "&:hover": {
            backgroundColor: "action.hover",
          },
          ".MuiCardHeader-action": {
            alignSelf: "center",
          },
        }}
      />
      <CardContent>
        <Collapse in={expanded}>
          <Box textAlign="center" p={3}>
            {loading ? (
              <CircularProgress sx={{ mb: 4 }} />
            ) : signature ? (
              <Box
                component="img"
                src={signature}
                alt="Digital Signature"
                sx={{
                  maxWidth: 500,
                  maxHeight: 200,
                  mb: 4,
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              />
            ) : (
              <>
                <BorderColorIcon sx={{ fontSize: 60, mb: 4 }} />
                <Typography color="textSecondary" gutterBottom>
                  No digital signature found
                </Typography>
              </>
            )}
            <Box display="flex" justifyContent="center" gap={2} mt={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<BorderColorIcon />}
                onClick={() => setOpenDialog(true)}
                disabled={loading}
              >
                Create Signature
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current.click()}
                disabled={loading}
              >
                Upload Signature
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={autoApproved}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setAutoApproved(newValue);
                  updateAutoApprove({
                    variables: { autoApproved: newValue },
                  }).catch(() => {
                    setAutoApproved(!newValue);
                  });
                }}
                disabled={!signature}
              />
            }
            label="Automatically approve all pending orders"
          />
        </Collapse>
      </CardContent>
      <CreateSignatureDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={(dataUrl) => {
          handleSaveSignature(dataUrl);
          setOpenDialog(false);
        }}
      />
    </Card>
  );
}
